import { arrayUnion } from "firebase/firestore";
import { dbService as db } from "../firebaseInstance";
import { fetchThreadsForPeriod } from "./pureDataFetch";
import moment from "moment";
import { 
  determineTargetDocument,
  updateThreadsMetadata,
  getNextDocumentNumber 
} from './firestoreShardingUtils';

/**
 * API를 통해 특정 날짜 범위의 스레드를 가져오는 함수 (단순하게)
 * @param {string} since - ISO 8601 형식의 시작 날짜
 * @param {string} until - ISO 8601 형식의 종료 날짜
 * @param {Function} onProgress - 진행 상황 콜백
 * @returns {Promise<Array>} 스레드 배열
 */
export const fetchThreadsFromAPI = async (since, until, onProgress = null) => {
  // ISO 날짜를 Unix timestamp로 변환
  let sinceTimestamp = moment(since).unix();
  let untilTimestamp = moment(until).unix();
  // Threads API 하한(1688540400)으로 클램프
  const MIN_SINCE = 1688540400;
  if (sinceTimestamp < MIN_SINCE) sinceTimestamp = MIN_SINCE;
  
  // until이 현재 시간보다 미래면 현재 시간으로 제한
  const now = Math.floor(Date.now() / 1000);
  if (untilTimestamp > now) {
    untilTimestamp = now;
  }

  try {
    if (onProgress) {
      onProgress({
        stage: 'fetching',
        message: 'API에서 스레드 데이터를 가져오는 중...'
      });
    }

    // 기존 fetchThreadsForPeriod 함수 한번만 호출 - 객체를 반환하므로 .threads로 접근
    const result = await fetchThreadsForPeriod(sinceTimestamp, untilTimestamp);
    const threads = result.threads || [];

    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: `총 ${threads.length}개 스레드 가져오기 완료`
      });
    }

    return threads;
  } catch (error) {
    console.error('Error fetching threads from API:', error);
    throw error;
  }
};

/**
 * 기존 스레드와 새 스레드를 비교하여 고유한 스레드만 반환
 * @param {Array} existingThreads - 기존 스레드 배열
 * @param {Array} newThreads - 새로 가져온 스레드 배열
 * @returns {Array} 중복이 제거된 고유 스레드 배열
 */
export const filterUniqueThreads = (existingThreads, newThreads) => {
  if (!Array.isArray(existingThreads) || !Array.isArray(newThreads)) {
    console.warn('Invalid input: arrays expected');
    return [];
  }

  const existingIds = new Set(existingThreads.map(thread => thread.id));
  const uniqueThreads = newThreads.filter(thread => {
    if (!thread || !thread.id) {
      console.warn('Invalid thread found:', thread);
      return false;
    }
    return !existingIds.has(thread.id);
  });

  console.log(`중복 제거 결과:`, {
    기존스레드수: existingThreads.length,
    새스레드수: newThreads.length,
    고유스레드수: uniqueThreads.length,
    중복제거수: newThreads.length - uniqueThreads.length
  });

  return uniqueThreads;
};

/**
 * Firestore에서 현재 저장된 스레드 가져오기
 * @param {string} userID - 사용자 ID
 * @returns {Promise<Array>} 현재 저장된 스레드 배열
 */
export const getExistingThreadsFromFirestore = async (userID) => {
  try {
    const userRef = db.collection('user').doc(userID);
    // threads 서브컬렉션의 모든 문서를 스캔하여 병합
    const snapshot = await userRef.collection('threads').get();

    const all = [];
    snapshot.forEach(doc => {
      const data = doc.data();
      if (data && Array.isArray(data.list)) {
        all.push(...data.list);
      }
    });

    return all;
  } catch (error) {
    console.error('Error fetching existing threads:', error);
    return [];
  }
};


/**
 * 고유 스레드를 Firestore에 업데이트 (청크 단위 처리로 1MB 초과 방지)
 * @param {string} userID - 사용자 ID
 * @param {Array} uniqueThreads - 중복 제거된 고유 스레드 배열
 * @returns {Promise<void>}
 */
export const updateThreadsToFirestore = async (userID, uniqueThreads) => {
  if (!uniqueThreads || uniqueThreads.length === 0) {
    console.log('No unique threads to update');
    return;
  }

  try {
    const userRef = db.collection("user").doc(userID);
    
    // ✅ 저장 전 1차 ID 기반 중복 제거 (기존 Firestore와 비교)
    const existingThreads = await getExistingThreadsFromFirestore(userID);
    const existingIds = new Set(existingThreads.map(t => t && t.id));
    const dedupedThreads = uniqueThreads.filter(t => t && !existingIds.has(t.id));
    if (dedupedThreads.length === 0) {
      console.log('❌ 모든 스레드가 이미 존재함 - 저장 중단');
      return;
    }
    
    // 1. 메타데이터 기반 타겟 문서 결정
    let currentTargetDoc = await determineTargetDocument(userID);
    
    // 2. 청크 단위로 분할 (50개씩)
    const CHUNK_SIZE = 50;
    const chunks = [];
    for (let i = 0; i < dedupedThreads.length; i += CHUNK_SIZE) {
      chunks.push(dedupedThreads.slice(i, i + CHUNK_SIZE));
    }
    
    console.log(`🔄 Processing ${dedupedThreads.length} threads in ${chunks.length} chunks (${CHUNK_SIZE} each)`);
    
    // 3. 각 청크 처리
    let totalProcessed = 0;
    
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i];
      console.log(`📦 Processing chunk ${i + 1}/${chunks.length} with ${chunk.length} threads`);
      
      try {
        // 타겟 문서에 업데이트 시도
        const threadsRef = userRef.collection("threads").doc(currentTargetDoc);
        
        await threadsRef.update({
          list: arrayUnion(...chunk)
        }).catch(async (error) => {
          // 문서가 없으면 생성
          if (error.code === 'not-found') {
            await threadsRef.set({
              list: chunk,
              createdAt: new Date().toISOString()
            });
          } else {
            throw error;
          }
        });
        
        totalProcessed += chunk.length;
        console.log(`✅ Chunk ${i + 1} processed successfully. Total: ${totalProcessed}/${dedupedThreads.length}`);
        
      } catch (error) {
        if (error.message.includes('exceeds the maximum allowed size')) {
          console.log(`⚠️ 1MB limit exceeded on ${currentTargetDoc}. Creating new document...`);
          
          // 새 문서로 전환
          currentTargetDoc = getNextDocumentNumber(currentTargetDoc);
          
          // 새 문서에 현재 청크 저장
          await userRef.collection("threads").doc(currentTargetDoc).set({
            list: chunk,
            createdAt: new Date().toISOString()
          });
          
          // 메타데이터 업데이트
          
          await updateThreadsMetadata(userID, {
            nextTargetDocument: currentTargetDoc,
            isApproachingLimit: false // 새 문서이므로 리셋
          });
          
          totalProcessed += chunk.length;
          console.log(`✅ Created new document ${currentTargetDoc} and processed chunk ${i + 1}. Total: ${totalProcessed}/${dedupedThreads.length}`);
          
        } else {
          console.error(`❌ Error processing chunk ${i + 1}:`, error);
          throw error;
        }
      }
    }
    
    // 4. 사용자 문서의 lastUpdated 업데이트
    await userRef.update({
      lastUpdated: Math.floor(Date.now() / 1000),
      manualUpdateHistory: arrayUnion({
        date: new Date().toISOString(),
        threadsAdded: dedupedThreads.length,
        type: 'manual_date_range_update',
        chunksProcessed: chunks.length,
        documentsUsed: currentTargetDoc
      })
    });

    console.log(`🎉 Successfully updated ${dedupedThreads.length} threads to Firestore using ${chunks.length} chunks`);
    
  } catch (error) {
    console.error('❌ Error updating threads to Firestore:', error);
    throw error;
  }
};

/**
 * 날짜 범위 기반 스레드 가져오기 및 중복 확인
 * @param {string} userID - 사용자 ID
 * @param {string} since - ISO 8601 형식의 시작 날짜
 * @param {string} until - ISO 8601 형식의 종료 날짜
 * @param {Function} onProgress - 진행 상황 콜백
 * @returns {Promise<Object>} 가져온 스레드 정보
 */
export const fetchAndCompareThreads = async (userID, since, until, onProgress = null) => {
  const result = {
    success: false,
    totalFetched: 0,
    uniqueAdded: 0,
    duplicatesSkipped: 0,
    error: null,
    threads: []
  };

  try {
    // 1. API에서 날짜 범위의 스레드 가져오기
    if (onProgress) {
      onProgress({
        stage: 'fetching',
        message: 'API에서 스레드 데이터를 가져오는 중...'
      });
    }

    const newThreads = await fetchThreadsFromAPI(since, until, onProgress);
    result.totalFetched = newThreads.length;

    // 2. 기존 Firestore 데이터 가져오기
    if (onProgress) {
      onProgress({
        stage: 'comparing',
        message: '기존 데이터와 비교 중...'
      });
    }

    const existingThreads = await getExistingThreadsFromFirestore(userID);

    // 3. 중복 제거
    const uniqueThreads = filterUniqueThreads(existingThreads, newThreads);
    result.uniqueAdded = uniqueThreads.length;
    result.duplicatesSkipped = result.totalFetched - result.uniqueAdded;
    result.threads = uniqueThreads;

    if (onProgress) {
      onProgress({
        stage: 'complete',
        message: uniqueThreads.length === 0 ? '새로운 스레드가 없습니다.' : '스레드 조회 완료!'
      });
    }

    result.success = true;
  } catch (error) {
    console.error('Thread fetch failed:', error);
    result.error = error.message;
    
    if (onProgress) {
      onProgress({
        stage: 'error',
        message: `조회 실패: ${error.message}`
      });
    }
  }

  return result;
};